import type { InsuranceProduct, InsuranceProductVersion } from '../../model/model.ts'

import type { EsMrpaxeActivityConfiguration } from './es-mrpaxe-activity-configuration.ts'
import type { EsMrpaxePolicyItem, EsMrpaxePolicyItemsState } from './es-mrpaxe-insurance.ts'
import type { EsMrpaxePricingVersion } from './es-mrpaxe-pricing-versions.ts'
import type {
  EsMrpaxeActivitiesConfigVersionFormat,
  EsMrpaxeBaseName,
  EsMrpaxeProductVersionFormat,
  EsMrpaxeProductVersionPrefix,
  EsMrpaxeStaticFileIdFormat,
} from './model.ts'
import { esMrpaxePolicyItems_V1_0_0 } from './v1_0_0/policy-items.ts'
import { esMrpaxeStaticFilesIds_v1_0_0 } from './v1_0_0/static-files.ts'

export const esMrpaxeProductName = 'product-es-mrp-axeria'
export type EsMrpaxeProductName = typeof esMrpaxeProductName

export const esMrpaxeProductVersions = [
  /**
   * Initial version: es-mrpw 1.1.0 (first-year annual commitment on contracts managed by Orus), carried by Axeria.
   */
  'product-es-mrp-axeria-1.0.0-2026-10-11',
] as const satisfies EsMrpaxeProductVersionFormat[]

export type EsMrpaxeProductVersion = (typeof esMrpaxeProductVersions)[number]

type EsMrpaxeInsuranceProductVersion = InsuranceProductVersion<
  EsMrpaxeProductVersionPrefix,
  EsMrpaxeBaseName,
  EsMrpaxeProductVersion,
  EsMrpaxePolicyItem,
  EsMrpaxeStaticFileIdFormat,
  EsMrpaxePricingVersion,
  EsMrpaxeActivitiesConfigVersionFormat,
  EsMrpaxePolicyItemsState
>

type EsMrpaxeInsuranceProduct = InsuranceProduct<
  EsMrpaxeProductVersionPrefix,
  EsMrpaxeBaseName,
  EsMrpaxeProductVersion,
  EsMrpaxePolicyItem,
  EsMrpaxeStaticFileIdFormat,
  EsMrpaxePricingVersion,
  EsMrpaxeActivitiesConfigVersionFormat,
  EsMrpaxePolicyItemsState
>

export const esMrpaxeProduct_v1_0_0 = {
  version: 'product-es-mrp-axeria-1.0.0-2026-10-11',
  description: 'Initial version of ES-MRPAXE product',
  policyItems: esMrpaxePolicyItems_V1_0_0,
  staticFilesId: esMrpaxeStaticFilesIds_v1_0_0,
  pricingVersion: ['pricing-es-mrp-axeria-1.0.0-2026-10-11', 'pricing-es-mrp-axeria-1.1.0-2026-11-06'],
  activitiesConfigVersion: 'activities-config-es-mrp-axeria-1.0.0-2026-10-11',
} as const satisfies EsMrpaxeInsuranceProductVersion

export const esMrpaxeProduct = {
  id: 'es-mrpaxe',
  versions: {
    'product-es-mrp-axeria-1.0.0-2026-10-11': esMrpaxeProduct_v1_0_0,
  },
} as const satisfies EsMrpaxeInsuranceProduct
export type EsMrpaxeProduct = typeof esMrpaxeProduct

/**
 * Whether one of the activity configurations has the segment or sub-segment.
 */
export function hasEsMrpaxeActivitySegmentOrSubSegment(
  configurations: readonly Pick<EsMrpaxeActivityConfiguration, 'segment' | 'subSegment'>[] | null | undefined,
  segmentOrSubSegment: string,
): boolean {
  if (!configurations) return false
  return configurations.some(
    ({ segment, subSegment }) => segment === segmentOrSubSegment || subSegment === segmentOrSubSegment,
  )
}
